import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/AppIcon';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';

const Terms = () => {
  const seoData = {
    title: 'Terms of Service - OctalChip | Web & Mobile App Development',
    description: 'Terms of Service for OctalChip - Read our terms and conditions for using our web and mobile app development services.',
    keywords: 'terms of service, terms and conditions, legal terms, OctalChip, web development, mobile app development, service agreement, client terms',
    canonical: 'https://www.octalchip.com/terms',
    tags: ['terms of service', 'terms and conditions', 'legal terms', 'OctalChip'],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' }
    ],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Terms of Service', url: 'https://www.octalchip.com/terms' }
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms of Service - OctalChip",
      "description": "Terms of Service for OctalChip - Read our terms and conditions for using our web and mobile app development services.",
      "url": "https://www.octalchip.com/terms",
      "mainEntity": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "404, Opera point, kothariya",
          "addressLocality": "Rajkot",
          "addressRegion": "Gujarat",
          "postalCode": "360022",
          "addressCountry": "IN"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+917574082582",
          "contactType": "customer service",
          "email": "business@octalchip.com"
        }
      }
    }
  };

  return (
    <>
      <SEO {...seoData} />
      
      <Header />
      
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-4">
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-gray-600">Last updated: January 27, 2025</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Welcome to OctalChip. These Terms of Service ("Terms") govern your use of our website and services. By accessing or using our services, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If you do not agree to these Terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Services</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                OctalChip provides comprehensive web and mobile application development services, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Custom web application development</li>
                <li>Mobile app development (iOS and Android)</li>
                <li>E-commerce solutions</li>
                <li>Cloud-based applications</li>
                <li>API development and integration</li>
                <li>UI/UX design services</li>
                <li>Maintenance and support services</li>
                <li>Consulting and technical advisory services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Client Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                As a client, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide accurate and complete project requirements</li>
                <li>Respond promptly to requests for information and feedback</li>
                <li>Make timely payments as agreed in the project contract</li>
                <li>Provide necessary access to systems and resources</li>
                <li>Respect intellectual property rights</li>
                <li>Use the delivered solutions in accordance with applicable laws</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Payment Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Payment terms will be specified in individual project contracts. Generally:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Projects may require an upfront deposit (typically 30-50%)</li>
                <li>Milestone-based payments for larger projects</li>
                <li>Final payment due upon project completion and delivery</li>
                <li>Late payments may incur additional charges</li>
                <li>All prices are exclusive of applicable taxes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Intellectual property ownership will be clearly defined in project contracts. Generally:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Client retains ownership of their existing intellectual property</li>
                <li>Custom-developed solutions become client property upon full payment</li>
                <li>OctalChip retains rights to pre-existing tools, frameworks, and methodologies</li>
                <li>Open-source components remain under their respective licenses</li>
                <li>Third-party integrations subject to their respective terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Project Timeline and Deliverables</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Project timelines and deliverables will be specified in individual contracts. Factors affecting timelines include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Project complexity and scope</li>
                <li>Client response time and feedback</li>
                <li>Third-party dependencies and integrations</li>
                <li>Testing and quality assurance requirements</li>
                <li>Unexpected technical challenges</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Both parties agree to maintain confidentiality of sensitive information, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Business strategies and proprietary information</li>
                <li>Technical specifications and source code</li>
                <li>Client data and user information</li>
                <li>Financial information and pricing details</li>
                <li>Any other information marked as confidential</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>OctalChip's liability is limited to the total amount paid for the specific project</li>
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>We are not responsible for third-party service failures or data breaches</li>
                <li>Clients are responsible for backing up their data</li>
                <li>We provide solutions "as is" without warranties beyond those specified in contracts</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Warranty and Support</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We provide the following warranty and support:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>90-day warranty on delivered solutions for defects in workmanship</li>
                <li>Bug fixes and minor updates included during warranty period</li>
                <li>Ongoing support available through separate maintenance agreements</li>
                <li>Response time commitments specified in support contracts</li>
                <li>Emergency support available for critical issues</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Either party may terminate a project contract with written notice. Termination terms include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Payment for completed work up to termination date</li>
                <li>Return of client materials and data</li>
                <li>Confidentiality obligations continue after termination</li>
                <li>Intellectual property rights as specified in contract</li>
                <li>30-day notice period for ongoing maintenance contracts</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Force Majeure</h2>
              <p className="text-gray-700 leading-relaxed">
                Neither party shall be liable for delays or failures due to circumstances beyond their reasonable control, including natural disasters, government actions, pandemics, or other force majeure events. Affected timelines will be adjusted accordingly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                These Terms are governed by the laws of India. Any disputes will be resolved through:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Good faith negotiations between parties</li>
                <li>Mediation if negotiations fail</li>
                <li>Arbitration in Gujarat, India if mediation fails</li>
                <li>Courts of competent jurisdiction in Gujarat, India as last resort</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated "Last updated" date. Continued use of our services after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For questions about these Terms or our services, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>OctalChip</strong></p>
                <p className="text-gray-700 mb-2">404, Opera Point, Kothariya, Gujarat, India, 360022</p>
                <p className="text-gray-700 mb-2">Email: <a href="mailto:business@octalchip.com" className="text-primary hover:underline">business@octalchip.com</a></p>
                <p className="text-gray-700">Phone: <a href="tel:+917574082582" className="text-primary hover:underline">+91 7574082582</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default Terms;
